import { ApplicationsFlags } from "@telstra-connect/subscriptions/state/config-subscriptions";
import { IFeatureFlagWithBVT } from "../types";

/**
 * Determines if a specific feature is enabled for a user based on the BVT flag and user details.
 *
 * @param bvtFlag - The feature flag containing BVT information.
 * @param userCidn - The customer ID of the user for whom we are checking feature access.
 * @param isTestCustomer - An array of customer IDs that are designated as test customers.
 * @returns True if the feature is enabled for the user; otherwise, false.
 */
export function isFeatureEnabled(bvtFlag: IFeatureFlagWithBVT, userCidn: string, isTestCustomer: string[]): boolean {
    if (!isValidBvtFlag(bvtFlag)) {
        return false;
    }
    const {bvtEnabledFeature, flagState} = bvtFlag;
    if (bvtEnabledFeature && flagState) {
        return isBVTCustomer(isTestCustomer, userCidn);
    }
    return flagState;
}

/**
 * Checks if a given customer ID (cidn) is part of the test customers.
 *
 * @param isTestCustomer - An array of customer IDs that are designated as test customers.
 * @param cidn - The customer ID to check against the test customer list.
 * @returns True if the customer ID is found in the test customer list; otherwise, false.
 */
const isBVTCustomer = (isTestCustomer: string[], userCidn: string) => {
    return isTestCustomer?.length && userCidn && isTestCustomer.includes(userCidn) ? true : false;
}

/**
 * Validates the structure and types of a BVT feature flag.
 *
 * @param bvtFlag - The feature flag object that needs validation.
 * @returns True if the feature flag has valid properties; otherwise, false.
 */
const isValidBvtFlag = (bvtFlag: IFeatureFlagWithBVT) => {
    if (!bvtFlag || typeof bvtFlag !== "object") {
        return false;
    }
    return (
        typeof bvtFlag.flagState === "boolean" &&
        typeof bvtFlag.bvtEnabledFeature === "boolean"
    );
};

/**
 * Retrieves a BVT flag from the application configuration.
 *
 * @param applicationConfig The application configuration object.
 * @param appName The name of the application.
 * @param bvtFlagName The name of the BVT flag to retrieve.
 * @returns The BVT flag value if found, otherwise null.
 */
export const findBvtFlagFromApplicationConfig = (
    applicationConfig: ApplicationsFlags,
    appName: string,
    bvtFlagName: string
): IFeatureFlagWithBVT => {
    if (!applicationConfig || !applicationConfig[appName]) {
        return;
    }
    const appBasedFlags = applicationConfig[appName];
    if (appBasedFlags?.featureFlags && appBasedFlags.featureFlags[bvtFlagName]) {
        return appBasedFlags.featureFlags[bvtFlagName];
    }
    if (appBasedFlags[bvtFlagName]) {
        return appBasedFlags[bvtFlagName];
    }
    return;
};
